import { createInstance, dateReviver } from '@/api';
import {
    CreateProductEntityDto,
    ProductEntityClient,
    ProductEntityDetailDto,
    UpdateProductEntityDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductProcessDefinitionEditStore } from '../product-process-definition/product-process-definition-edit.store';
import { useProductEntityLinkStore } from '../product-entity-link/product-entity-link.store';
import { useProductTransformationStore } from '../product-transformation/product-transformation.store';
import { useProductEntityDataMappingStore } from '../product-entity-data-mapping/product-entity-data-mapping.store';

export interface ProductEntityStoreState {
    items?: { key: string; value: ProductEntityDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new ProductEntityClient('', createInstance(), dateReviver);

export const useProductEntityStore = defineStore('configuration-product-entity-store', {
    state: (): ProductEntityStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): ProductEntityDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        },
        bySpecification: (state) => {
            return (specificationId: number): ProductEntityDetailDto[] => {
                const key = `specification-${specificationId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async updateProductEntityUI(items: ProductEntityDetailDto[]) {
            items?.forEach((item) => {
                useProductEntityLinkStore().updateProductEntityUI(item);
                useProductTransformationStore().updateProductEntityUI(item);
                useProductEntityDataMappingStore().updateProductEntityUI(item);
            });
        },
        async getByDefinition(definitionId: number, reloading: boolean = false) {
            const key = `definition-${definitionId}`;
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateProductEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async getBySpecification(specificationId: number, reloading: boolean = false) {
            const key = `specification-${specificationId}`;
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                const response = await client.getBySpecification(specificationId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                this.updateProductEntityUI(response);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productentity.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(specificationId: number, dto: CreateProductEntityDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                useAlertStore().setSuccess(i18n.global.t('productentity.detail.success.create.title'));
                this.getBySpecification(specificationId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('productentity.detail.error.create.title'), error);
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(specificationId: number, entityId: number, dto: UpdateProductEntityDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.update(entityId, dto);
                useAlertStore().setSuccess(i18n.global.t('productentity.detail.success.update.title'));
                this.getBySpecification(specificationId, true);
                return response;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('productentity.detail.error.update.title'), error);
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(specificationId: number, entityId: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(entityId);
                useAlertStore().setSuccess(i18n.global.t('productentity.detail.success.delete.title'));
                this.getBySpecification(specificationId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productentity.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(specificationId: number) {
            const key = `specification-${specificationId}`;
            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
